import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Package, ShoppingCart, MapPin, TrendingUp, FileText, Handshake, ShieldCheck, FileCheck, AlertTriangle, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { tStatus } from '@/i18n';
import SPAActions from '@/components/SPAActions';

export default function SellerDashboard() {
  const { t } = useTranslation();
  const [myListings, setMyListings] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [myDeals, setMyDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState(null);
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [listingsRes, offersRes, dealsRes] = await Promise.all([
        axios.get(`${API_URL}/minerals/seller/my-listings`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/offers/seller/received`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/deals/my-deals`, { headers: getAuthHeader() })
      ]);
      setMyListings(listingsRes.data);
      setReceivedOffers(offersRes.data);
      setMyDeals(dealsRes.data);
    } catch { toast.error(t('error.generic')); }
    finally { setLoading(false); }
  }, [getAuthHeader, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRespondToOffer = async (offerId, status, counterOffer = null) => {
    try {
      await axios.post(`${API_URL}/offers/${offerId}/respond`, { status, counter_offer: counterOffer }, { headers: getAuthHeader() });
      toast.success(status === 'accepted' ? t('seller.offerAccepted') : t('seller.offerRejected'));
      fetchData();
    } catch { toast.error(t('error.generic')); }
  };

  const handleDeleteListing = async (mineralId, title) => {
    if (!window.confirm(t('seller.deleteConfirm'))) return;
    try {
      await axios.delete(`${API_URL}/minerals/${mineralId}`, { headers: getAuthHeader() });
      toast.success(t('seller.listingDeleted'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error.generic'));
    }
  };

  const handleViewSummary = async (dealId) => {
    try {
      const res = await axios.get(`${API_URL}/deals/${dealId}/summary`, { headers: getAuthHeader() });
      setCurrentSummary(res.data);
      setSummaryOpen(true);
    } catch { toast.error(t('error.generic')); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1 font-['Outfit']" data-testid="seller-dashboard-title">{t('seller.title')}</h1>
            <p className="text-[#94A3B8]">{t('seller.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/seller/market-insights')} data-testid="market-insights-button" className="bg-[#1A2440] text-white border border-[#2563EB]/30 hover:bg-[#2563EB]/20 hover:border-[#2563EB]">
              <TrendingUp className="w-4 h-4 mr-2" /> {t('insights.title')}
            </Button>
            <Button onClick={() => navigate('/seller/documents')} data-testid="documents-button" className="bg-[#1A2440] text-white border border-[#1E293B] hover:bg-white/5">
              <FileText className="w-4 h-4 mr-2" /> {t('doc.title')}
            </Button>
            <Button onClick={() => navigate('/seller/create-listing')} data-testid="create-listing-button" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">
              <Plus className="w-4 h-4 mr-2" /> {t('seller.createListing')}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="listings" className="w-full">
          <TabsList className="mb-6 bg-[#121A2F] border border-[#1E293B]">
            <TabsTrigger value="listings" data-testid="listings-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <Package className="w-4 h-4 mr-2" /> {t('seller.tabs.listings')} ({myListings.length})
            </TabsTrigger>
            <TabsTrigger value="offers" data-testid="offers-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <ShoppingCart className="w-4 h-4 mr-2" /> {t('seller.tabs.offers')} ({receivedOffers.length})
            </TabsTrigger>
            <TabsTrigger value="deals" data-testid="deals-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <Handshake className="w-4 h-4 mr-2" /> {t('seller.tabs.deals')} ({myDeals.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings" data-testid="listings-content">
            {loading ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F59E0B]"></div></div>
            ) : myListings.length === 0 ? (
              <div className="text-center py-20 bg-[#121A2F] rounded-xl border border-[#1E293B]">
                <Package className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                <p className="text-xl text-[#94A3B8] mb-4">{t('seller.empty.listings')}</p>
                <Button onClick={() => navigate('/seller/create-listing')} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">{t('seller.createListing')}</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myListings.map((mineral) => (
                  <div key={mineral.id} className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden hover:-translate-y-1 hover:border-[#F59E0B]/30 hover:shadow-[0_8px_30px_rgba(245,158,11,0.1)] transition-all duration-300" data-testid={`listing-card-${mineral.id}`}>
                    <div className="aspect-video bg-[#1A2440] overflow-hidden">
                      {mineral.images?.[0] ? (
                        <img src={mineral.images[0]} alt={mineral.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-[#1E293B]" /></div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="bg-[#10B981]/10 text-[#10B981] px-2.5 py-0.5 rounded-full text-xs font-medium border border-[#10B981]/20">{tStatus(mineral.status)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                            (mineral.listing_status || 'verified') === 'verified' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' :
                            mineral.listing_status === 'rejected' ? 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20' :
                            'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20'
                          }`} data-testid={`listing-status-${mineral.id}`}>{tStatus(mineral.listing_status || 'verified')}</span>
                        </div>
                        <span className="text-xl font-bold text-white">${mineral.price}<span className="text-sm font-normal text-[#64748B]">/{mineral.unit}</span></span>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{mineral.title}</h3>
                      <p className="text-[#64748B] text-sm mb-3 line-clamp-2">{mineral.description}</p>
                      <div className="flex items-center text-xs text-[#64748B] mb-1"><MapPin className="w-3 h-3 mr-1" />{mineral.location}</div>
                      <div className="text-xs text-[#64748B]">{t('common.quantity')}: <span className="font-medium text-[#94A3B8]">{mineral.quantity} {mineral.unit}</span></div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        {mineral.seller_verified && (
                          <span className="inline-flex items-center gap-0.5 text-[#10B981]" data-testid={`verified-badge-${mineral.id}`}>
                            <ShieldCheck className="w-3.5 h-3.5" /> {t('common.verified')}
                          </span>
                        )}
                        {mineral.verified_documents_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[#3B82F6]" data-testid={`docs-badge-${mineral.id}`}>
                            <FileCheck className="w-3.5 h-3.5" /> {mineral.verified_documents_count} {mineral.verified_documents_count > 1 ? t('featured.docsPlural') : t('featured.docs')}
                          </span>
                        )}
                      </div>
                      <div className="mt-4 pt-3 border-t border-[#1E293B]">
                        <Button
                          onClick={() => handleDeleteListing(mineral.id, mineral.title)}
                          data-testid={`delete-listing-${mineral.id}`}
                          className="w-full bg-transparent border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 text-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t('seller.deleteListing')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="offers" data-testid="offers-content">
            {loading ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F59E0B]"></div></div>
            ) : receivedOffers.length === 0 ? (
              <div className="text-center py-20 bg-[#121A2F] rounded-xl border border-[#1E293B]">
                <ShoppingCart className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                <p className="text-xl text-[#94A3B8]">{t('seller.empty.offers')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {receivedOffers.map((offer) => (
                  <div key={offer.id} className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-6" data-testid={`offer-card-${offer.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-white">{offer.mineral_title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            offer.status === 'pending' ? 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20' :
                            offer.status === 'accepted' ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20' :
                            offer.status === 'rejected' ? 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20' :
                            'bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20'
                          }`}>{tStatus(offer.status)}</span>
                        </div>
                        <p className="text-[#94A3B8] mb-1">{t('featured.by')}: <span className="font-medium text-white">{offer.buyer_name}</span></p>
                        <p className="text-sm text-[#64748B] mb-1">{t('common.quantity')}: {offer.quantity}</p>
                        {offer.compliance_required && (
                          <div className={`inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-md text-xs font-medium ${
                            offer.compliance_status === 'approved' ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20' :
                            offer.compliance_status === 'rejected' ? 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20' :
                            'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20'
                          }`} data-testid={`compliance-status-${offer.id}`}>
                            <AlertTriangle className="w-3.5 h-3.5" />
                            {t('admin.tabs.compliance')}: {tStatus(offer.compliance_status || 'pending')}
                          </div>
                        )}
                        {offer.message && <p className="text-sm text-[#64748B] italic mt-1">"{offer.message}"</p>}
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <p className="text-sm text-[#64748B]">{t('common.price')}</p>
                          <p className="text-2xl font-bold text-[#F59E0B]">${offer.offer_price}</p>
                        </div>
                        {offer.status === 'pending' && (
                          <div className="flex flex-col items-end gap-2">
                            {offer.compliance_required && offer.compliance_status !== 'approved' && offer.compliance_status !== 'not_required' ? (
                              <p className="text-xs text-[#F59E0B] text-right max-w-[200px]">{t('status.under_review')}</p>
                            ) : (
                              <Button onClick={() => handleRespondToOffer(offer.id, 'accepted')} data-testid={`accept-offer-${offer.id}`} className="bg-[#10B981] hover:bg-[#059669] text-white">{t('seller.acceptOffer')}</Button>
                            )}
                            <Button onClick={() => handleRespondToOffer(offer.id, 'rejected')} data-testid={`reject-offer-${offer.id}`} className="bg-transparent border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10">{t('seller.reject')}</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="deals" data-testid="deals-content">
            {loading ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F59E0B]"></div></div>
            ) : myDeals.length === 0 ? (
              <div className="text-center py-20 bg-[#121A2F] rounded-xl border border-[#1E293B]">
                <Handshake className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                <p className="text-xl text-[#94A3B8]">{t('seller.empty.deals')}</p>
              </div>
            ) : (
              <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                      <tr>
                        {[t('detail.specs'), t('detail.seller'), t('common.price'), t('common.quantity'), 'Total', t('common.status'), t('common.date'), ''].map((h, i) => (
                          <th key={i} className="px-6 py-4 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {myDeals.map((deal) => (
                        <tr key={deal.id} className="border-b border-[#1E293B] hover:bg-white/[0.02] transition-colors" data-testid={`deal-row-${deal.id}`}>
                          <td className="px-6 py-4 text-sm font-medium text-white">{deal.mineral_title}</td>
                          <td className="px-6 py-4 text-sm text-[#94A3B8]">{deal.buyer_name}</td>
                          <td className="px-6 py-4 text-sm text-white">${deal.agreed_price.toLocaleString()}</td>
                          <td className="px-6 py-4 text-sm text-[#94A3B8]">{deal.quantity}</td>
                          <td className="px-6 py-4 text-sm font-medium text-[#F59E0B]">${deal.total_value.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              deal.status === 'completed' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' :
                              deal.status === 'verified' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20' :
                              deal.status === 'in_progress' ? 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20' :
                              deal.status === 'cancelled' ? 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20' :
                              'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20'
                            }`}>{tStatus(deal.status)}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#64748B]">{new Date(deal.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4">
                            {deal.status !== 'pending_verification' && (
                              <div className="flex flex-wrap items-center gap-2">
                                <Button onClick={() => navigate(`/deals/${deal.id}`)} data-testid={`open-deal-${deal.id}`} size="sm" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">
                                  Open Deal
                                </Button>
                                <Button onClick={() => handleViewSummary(deal.id)} data-testid={`view-summary-${deal.id}`} size="sm" className="bg-[#1A2440] text-white border border-[#1E293B] hover:bg-white/5">
                                  <Eye className="w-3.5 h-3.5 mr-1" /> {t('common.view')}
                                </Button>
                                <SPAActions deal={deal} onUpdated={fetchData} />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
          <DialogContent className="bg-[#121A2F] border-[#1E293B] max-w-lg" data-testid="deal-summary-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F59E0B]" /> Deal Agreement Summary
              </DialogTitle>
            </DialogHeader>
            {currentSummary && (
              <div className="mt-4 bg-[#0B1120] rounded-xl border border-[#1E293B] overflow-hidden">
                <div className="bg-[#1A2440] px-5 py-3 border-b border-[#1E293B]">
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Deal #{currentSummary.deal_id?.slice(0, 8)}</p>
                  <p className="text-lg font-bold text-white">{currentSummary.mineral_title}</p>
                </div>
                <div className="divide-y divide-[#1E293B]">
                  {[
                    ['Mineral Type', currentSummary.mineral_type],
                    ['Quantity', `${currentSummary.quantity} ${currentSummary.unit}`],
                    ['Price per Unit', `$${currentSummary.agreed_price_per_unit?.toLocaleString()} ${currentSummary.currency}`],
                    ['Total Value', `$${currentSummary.total_value?.toLocaleString()} ${currentSummary.currency}`],
                    ['Buyer', currentSummary.buyer_name],
                    ['Seller', currentSummary.seller_name],
                    ['Location', currentSummary.location],
                    ['Grade', currentSummary.grade],
                    ['Delivery Terms', currentSummary.delivery_terms],
                    ['Payment Terms', currentSummary.payment_terms],
                  ].filter(([, val]) => val).map(([label, value]) => (
                    <div key={label} className="flex justify-between px-5 py-3">
                      <span className="text-sm text-[#64748B]">{label}</span>
                      <span className={`text-sm font-medium text-right max-w-[60%] ${label === 'Total Value' ? 'text-[#F59E0B]' : 'text-white'}`}>{value}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 bg-[#1A2440] border-t border-[#1E293B]">
                  <p className="text-xs text-[#64748B]">Generated: {new Date(currentSummary.generated_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
